<template>
	<div
		class="app-date-picker"
		v-on-clickaway="closePicker"
	>
		<div
			v-if="$slots.default"
			class="app-date-picker__label"
		>
			<slot></slot>
		</div>

		<div class="app-date-picker__wrapper">
			<div
				class="app-date-picker__toggle"
				:class="{
					'app-date-picker__toggle--disabled' : $attrs.disabled,
					'app-date-picker__toggle--error' : hasError
				}"
				@click="open = !open"
			>
				<input
					type="text"
					class="app-date-picker__input"
					v-bind="$attrs"
					@invalid="setInvalidMessage"
					:value="displayValue"
				>

				<AppIcon
					class="app-date-picker__icon"
					icon-name="calendar"
					icon-color="#778395"
				/>
			</div>

			<div
				v-if="open"
				class="app-date-picker__picker"
				:class="{'app-date-picker__picker--top' : getPositionReference().isTop,
						'app-date-picker__picker--right' : getPositionReference().isRight}"
			>
				<div class="picker">
					<p class="picker__label">From</p>
					<AppCalendar
						class="picker__calendar"
						:value="from"
						:min="min"
						:max="max"
						@input="updateValue({from: $event})"
					/>
				</div>

				<div class="picker">
					<p class="picker__label">To</p>
					<AppCalendar
						class="picker__calendar"
						:value="to"
						:min="from ? from : null"
						:max="max"
						@input="updateValue({to: $event})"
					/>
				</div>
			</div>
		</div>

		<p
			v-if="hasError"
			class="app-date-picker__error"
		>
			{{ error || invalidMessage }}
		</p>
	</div>
</template>

<script>

export default {
	inheritAttrs: false,

	props: {
		value: {
			type: Array,
			validator ([from, to]) {
				if (from && !(from instanceof Date)) return false
				if (to && !(to instanceof Date)) return false
				return true
			}
		},
		min: Date,
		max: Date,
		error: String
	},

	data () {
		return {
			dateValue: null,
			open: false,
			invalidMessage: null,
			from: null,
			to: null
		}
	},

	watch: {
		from (val) {
			if (val && this.to) {
				const dateFrom = val.setHours(0, 0, 0, 0)
				const dateTo = this.to.setHours(0, 0, 0, 0)
				if (dateFrom > dateTo) {
					this.to = val
					this.updateValue()
				}
			}
		},

		value: {
			handler	(val) {
				if (val && val.length) {
					this.from = val[0] || null
					this.to = val[1] || null
				} else {
					this.from = null
					this.to = null
				}
			},

			immediate: true
		}
	},

	computed: {
		hasError () {
			return this.error || this.invalidMessage
		},

		displayValue () {
			if (!this.value || !this.value.length) return ''
			const dateFrom = this.value[0] ? this.parseDate(new Date(this.value[0])) : null
			const dateTo = this.value[1] ? this.parseDate(new Date(this.value[1])) : null

			const dateFromString = dateFrom ? `${dateFrom.month} ${dateFrom.date}, ${dateFrom.year}` : ''
			const dateToString = dateTo ? `${dateTo.month} ${dateTo.date}, ${dateTo.year}` : ''

			return `${dateFromString} - ${dateToString}`
		}
	},

	methods: {
		updateValue (newVal = {}) {
			this.invalidMessage = null
			if (Object.prototype.hasOwnProperty.call(newVal, 'from')) this.from = newVal['from']
			if (Object.prototype.hasOwnProperty.call(newVal, 'to')) this.to = newVal['to']
			if (!this.from && !this.to) return this.$emit('input', null)
			this.$emit('input', [this.from, this.to])
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) {
				this.invalidMessage = 'Fill up this field'
			} else {
				this.invalidMessage = event.target.validationMessage
			}
		},

		parseDate (value) {
			const year = value.getFullYear()
			const month = value.toLocaleString(undefined, { month: 'short' })
			const date = String(value.getDate()).padStart(2, '0')
			return { year, month, date }
		},

		getPositionReference () {
			const elementPosition = this.$el.getBoundingClientRect()
			const isTop = window.innerHeight - elementPosition.top < elementPosition.top
			const isRight = window.innerWidth - elementPosition.left < elementPosition.left
			return { isTop, isRight }
		},

		closePicker () {
			this.open = false
		}
	}
}
</script>

<style lang="scss" scoped>
.app-date-picker {
	&__label {
		display: inline-block;
		font-size: 1em;
		margin-bottom: .5em;
	}

	&__wrapper {
		margin: 0;
		padding: 0;
		position: relative;
		color: $default;
	}

	&__toggle {
		font-size: 1em;
		border-radius: 3px;
		padding: 5px 8px;
		border: 1px solid $default-light;
		display: flex;
		align-items: center;
		cursor: pointer;

		&--disabled {
			pointer-events: none;
			opacity: 0.5;
		}

		&--error {
			border-color: $danger;
		}
	}

	&__input {
		flex: 1 1 auto;
		font-size: 1em;
		line-height: 1em;
		margin-right: 8px;
		border: none;
		outline: none;
		color: $default;
		pointer-events: none;
		background-color: #fff;

		&::placeholder {
			color: $default-light;
		}
	}

	&__icon {
		width: 1em;
		flex-shrink: 0;
	}

	&:hover &__toggle {
		border-color: $primary;

		&--disabled {
			border-color: $default-light;
		}

		&--error {
			border-color: $danger;
		}
	}

	&__picker {
		position: absolute;
		z-index: 999;
		top: 100%;
		left: 0;
		margin: 5px 0;
		box-shadow: 0 0 3px $default-light;
		background-color: #fff;
		display: flex;

		&--top {
			top: auto;
			bottom: 100%;
		}

		&--right {
			left: auto;
			right: 0;
		}

		.picker {
			&__label {
				text-align: center;
				padding: .5em;
			}

			&__calendar {
				margin: .5em;
			}
		}
	}

	&__error {
		color: $danger;
		margin-top: .5em;
		display: flex;
		justify-content: flex-end;
	}
}
</style>
